import type React from 'react';
import { Suspense, startTransition, useCallback, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { AI_SEARCH_INIT_PROMPT_PARAM } from '@xing-com/crate-jobs-constants';
import { useSerpsMatch } from '@xing-com/crate-jobs-domain-serps-hooks';
import { useSearchState } from '@xing-com/crate-jobs-hooks';
import { usePageTracking } from '@xing-com/crate-jobs-tracking';
import {
  AISearchErrorBanner,
  AIEarnMoreDialog,
} from '@xing-com/crate-jobs-xinglets';
import { ScrollMemo } from '@xing-com/crate-layout-scroll-memo-module';
import { SearchLoadingSkeleton } from '@xing-com/crate-search-utils';
import { NoSSR, NoSSRLoggedInSuspense } from '@xing-com/crate-utils-no-ssr';
import { XingletLoader } from '@xing-com/crate-xinglet';

import {
  FilterBottomSheet,
  AllFilters,
  FilterBar,
  Results,
  Header,
  BackButton,
  SearchBar,
} from './components';
import * as S from './container.styles';
import { useAddTypePolicies, useAIEarnMoreDialogProps } from './hooks';

export const Container: React.FC = () => {
  const { formatMessage } = useIntl();
  const { isLoggedOut } = useLoginState();
  const serps = useSerpsMatch();
  const isSerps = Boolean(isLoggedOut && serps);
  const { isEmptySearch } = useSearchState();

  const [isSearchBarVisible, setIsSearchBarVisible] = useState(true);
  const handleSearchBarInViewChange = useCallback((inView: boolean) => {
    startTransition(() => {
      setIsSearchBarVisible((previous) =>
        previous === inView ? previous : inView
      );
    });
  }, []);

  const aiEarnMoreDialogProps = useAIEarnMoreDialogProps();

  useAddTypePolicies();

  const { getSearchParameter } = useSearchParameter();
  const hasInitPrompt = !!getSearchParameter(AI_SEARCH_INIT_PROMPT_PARAM);
  const isEmptyState = isEmptySearch && !hasInitPrompt;

  usePageTracking({
    nwtOptions: {
      page: 'jobs_search/serp',
      flags: `jobs_search_ai_search_true,jobs_search_empty_state_${isEmptyState}`,
    },
  });

  return (
    <>
      <ScrollMemo />
      <div>
        <S.Background>
          <NoSSRLoggedInSuspense>
            <Header isSticky={!isSearchBarVisible} />
          </NoSSRLoggedInSuspense>
          <S.SearchBarContainer variant="regular" $isLoggedOut={isLoggedOut}>
            <InView
              as="div"
              initialInView
              onChange={handleSearchBarInViewChange}
            >
              <Suspense>
                <SearchBar />
              </Suspense>
            </InView>
            <NoSSRLoggedInSuspense>
              <FilterBar />
            </NoSSRLoggedInSuspense>
          </S.SearchBarContainer>
        </S.Background>
        <NoSSRLoggedInSuspense fallback={<SearchLoadingSkeleton />}>
          <S.ContentContainer variant="wide">
            <Results isStickyHeader={!isSearchBarVisible} />

            <S.LeftColumn>
              <nav>
                <BackButton />
              </nav>
              <aside
                aria-label={formatMessage({
                  id: 'JOBS_SEARCH_FILTERS_ALL_HEADER',
                })}
              >
                <AllFilters />
              </aside>
            </S.LeftColumn>
          </S.ContentContainer>

          {isSerps ? (
            <XingletLoader name="@xing-com/crate-jobs-domain-serps-container" />
          ) : null}
          <NoSSR>
            <FilterBottomSheet />
          </NoSSR>
          <AISearchErrorBanner />
          <AIEarnMoreDialog {...aiEarnMoreDialogProps} />
        </NoSSRLoggedInSuspense>
      </div>
    </>
  );
};
